import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Table, Tabs, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CaretDownOutlined, CaretUpOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilterPickup';
import MetricTooltip from '../../../components/dashboard/MetricTooltip';
import { QUERY_TMS_TRANSPORT_DASHBOARD } from '../../../graphql/queries/dashboard/queryTmsTransportDashboard';
import { QUERY_TMS_TRANSPORT_FILTER_OPTIONS } from '../../../graphql/queries/dashboard/queryTmsTransportFilterOptions';
import type { TmsTransportSummaryItem } from '../../../graphql/types/dashboard/TmsTransportDashboardTypes';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import { formatNumberWithCommas } from '../../../utils/numberUtils';
import { formatUtcToPacific } from '../../../utils/timezoneFormatter';
import TransportDetailDrawer, { TransportDetailContext } from './components/TransportDetailDrawer';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

const TIME_PERIOD_TOTAL = 'total';

type MetricKey =
    | 'tripCount'
    | 'rawDepartPackageQty'
    | 'departPackageQty'
    | 'avgWeightedPackageQty'
    | 'avgPackageQty'
    | 'departLoadRate'
    | 'avgLoadRate'
    | 'departWeightLoadRate'
    | 'onTimeArrivalRate'
    | 'onTimeDepartRate'
    | 'avgOverDriveDuration';

interface MetricConfig {
    key: MetricKey;
    title: string;
    description: string;
    wowKey: string;
    type: 'count' | 'integer' | 'decimal' | 'rate';
}

const METRIC_CONFIGS: MetricConfig[] = [
    { key: 'tripCount', title: '车次', description: '统计周期内，路线运输的车次', wowKey: 'tripCountWow', type: 'count' },
    { key: 'rawDepartPackageQty', title: '发车包裹量', description: '统计周期内，起点发车时装载的包裹总量', wowKey: 'rawDepartPackageQtyWow', type: 'integer' },
    { key: 'departPackageQty', title: '车均发车包裹量', description: '统计周期内，平均每车次起点发车时装载的包裹量', wowKey: 'departPackageQtyWow', type: 'integer' },
    { key: 'avgWeightedPackageQty', title: '车均加权平均包裹量', description: '统计周期内，平均每车次全程加权平均后装载的包裹量', wowKey: 'avgWeightedPackageQtyWow', type: 'integer' },
    // { key: 'avgPackageQty', title: '车均包裹量', description: '统计周期内，平均每车次全程加权平均后装载的包裹量', wowKey: 'avgPackageQtyWow', type: 'integer' },
    { key: 'departLoadRate', title: '车均发车装载率', description: '统计周期内，平均每车次起点发车时的装载率', wowKey: 'departLoadRateWow', type: 'rate' },
    { key: 'avgLoadRate', title: '车均加权平均装载率', description: '统计周期内，平均每车次全程加权平均后的装载率', wowKey: 'avgLoadRateWow', type: 'rate' },
    { key: 'departWeightLoadRate', title: '发车重量满载率', description: '统计周期内，平均每车次起点发车时所装载包裹的重量满载率', wowKey: 'departWeightLoadRateWow', type: 'rate' },
    { key: 'onTimeArrivalRate', title: '准时到达率', description: '统计周期内，司机按照线路时效准时到达目的地的车次 / 总车次', wowKey: 'onTimeArrivalRateWow', type: 'rate' },
    { key: 'onTimeDepartRate', title: '准时发车率', description: '统计周期内，司机按照线路动态时效准时从起点出发的车次 / 总车次', wowKey: 'onTimeDepartRateWow', type: 'rate' },
    { key: 'avgOverDriveDuration', title: '车均行驶时长超时量', description: '统计周期内，行驶时长超时量 / 总车次', wowKey: 'avgOverDriveDurationWow', type: 'decimal' },
];

interface RowMetrics {
    [period: string]: Partial<TmsTransportSummaryItem>;
}

interface TableRow {
    key: string;
    label: string;
    level: number;
    startSortingId: number;
    routeId: number;
    allRouteIds?: number[];  // 同名路线的全部 ID（用于下钻时查所有）
    segmentName: string;
    canDrillDown: boolean;
    metrics: RowMetrics;
    children?: TableRow[];
}

// 控制是否展示分段层，设为 false 时隐藏（代码保留）
const SHOW_SEGMENTS = false;

// 分段行仅展示这三个指标，其余显示 "-"
const SEGMENT_ALLOWED_METRICS: Set<MetricKey> = new Set(['onTimeArrivalRate', 'onTimeDepartRate', 'avgOverDriveDuration']);

const formatMetric = (value: string | number | null | undefined, type: MetricConfig['type']): string => {
    if (value === null || value === undefined) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    switch (type) {
        case 'count':
            return formatNumberWithCommas(num);
        case 'integer':
            return formatNumberWithCommas(Math.round(num));
        case 'decimal':
            return formatNumberWithCommas(num, 2);
        case 'rate':
            return `${formatNumberWithCommas(num, 2)}%`;
    }
};

const formatWow = (wow: string | null | undefined): React.ReactNode => {
    if (!wow) return '-';
    const num = parseFloat(wow);
    if (isNaN(num)) return '-';
    const abs = Math.abs(num);
    const label = `${formatNumberWithCommas(abs, 2)}%`;
    if (num > 0) return <span style={{ color: '#52c41a' }}><CaretUpOutlined />{label}</span>;
    if (num < 0) return <span style={{ color: '#ff4d4f' }}><CaretDownOutlined />{label}</span>;
    return <span>{label}</span>;
};

// 选择范围内最后一期的 period key，与后端 toEndPeriodKey 格式一致
function toLastPeriodKey(queryType: ScaleQueryType, endDate: string): string {
    if (queryType === ScaleQueryType.WEEKLY) {
        const end = dayjs(endDate);
        const monday = end.subtract((end.day() + 6) % 7, 'day');
        return `${monday.format('YYYY-MM-DD')}~${monday.add(6, 'day').format('YYYY-MM-DD')}`;
    }
    if (queryType === ScaleQueryType.MONTHLY) return endDate.slice(0, 7);
    return endDate;
}

// 合并同名路线/分段的 RowMetrics：全部从原始分子分母精确计算
function mergeRowMetrics(metricMaps: RowMetrics[], lastPeriodKey: string): RowMetrics {
    if (metricMaps.length === 1) return metricMaps[0];
    const allPeriods = new Set(metricMaps.flatMap(m => Object.keys(m)));
    const result: RowMetrics = {};

    // 求和辅助：统一用 Number() 转换，避免字符串拼接（如 "2"+"1"="21" 而非 3）
    const sumL = (items: Partial<TmsTransportSummaryItem>[], key: keyof TmsTransportSummaryItem): number =>
        items.reduce((s, i) => s + (Number(i[key]) || 0), 0);
    const sumS = (items: Partial<TmsTransportSummaryItem>[], key: keyof TmsTransportSummaryItem): number =>
        items.reduce((s, i) => { const v = i[key]; return s + (v != null ? Number(v) || 0 : 0); }, 0);
    const rate = (num: number, den: number) => den > 0 ? (num / den * 100).toFixed(2) : null;
    const div2 = (num: number, den: number) => den > 0 ? (num / den).toFixed(2) : null;
    const wowChange = (cur: number, prev: number) => prev !== 0 ? ((cur - prev) / Math.abs(prev) * 100).toFixed(2) : null;
    const wowDiff = (cur: number, den: number, pCur: number, pDen: number) => {
        const c = den > 0 ? cur / den * 100 : null;
        const p = pDen > 0 ? pCur / pDen * 100 : null;
        return c != null && p != null ? (c - p).toFixed(2) : null;
    };
    const avgWow = (num: number, den: number, pNum: number, pDen: number) =>
        den > 0 && pDen > 0 ? wowChange(num / den, pNum / pDen) : null;

    // 环比本期侧取最后一期（与后端口径一致：最后一期 vs 上一期），上期侧取 total 项的 prev 字段
    const lastItems = metricMaps.map(m => m[lastPeriodKey]).filter(Boolean) as Partial<TmsTransportSummaryItem>[];
    const hasLast = lastItems.length > 0;
    const lTrip = sumL(lastItems, 'tripCount');
    const lDPkg = sumL(lastItems, 'rawDepartPackageQty');
    const lAPkg = sumS(lastItems, 'rawAvgPackageQty');
    const lDLoadNum = sumL(lastItems, 'rawDepartLoadNumerator');
    const lALoadNum = sumL(lastItems, 'rawAvgLoadNumerator');
    const lLoadMax = sumL(lastItems, 'rawPackageLoadMaxLimit');
    const lActW = sumL(lastItems, 'rawActualLoadWeight');
    const lTheW = sumL(lastItems, 'rawTheoryLoadWeight');
    const lOnTimeArr = sumL(lastItems, 'rawOnTimeArrivalTripCount');
    const lOnTimeDepart = sumL(lastItems, 'rawOnTimeDepartTripCount');
    const lOverDrive = sumS(lastItems, 'rawOverDriveDurationCount');

    for (const period of allPeriods) {
        const items = metricMaps.map(m => m[period]).filter(Boolean) as Partial<TmsTransportSummaryItem>[];
        if (items.length === 0) continue;

        // 当期原始值求和
        const trip = sumL(items, 'tripCount');
        const dPkg = sumL(items, 'rawDepartPackageQty');
        const aPkg = sumS(items, 'rawAvgPackageQty');
        const dLoadNum = sumL(items, 'rawDepartLoadNumerator');
        const aLoadNum = sumL(items, 'rawAvgLoadNumerator');
        const loadMax = sumL(items, 'rawPackageLoadMaxLimit');
        const actW = sumL(items, 'rawActualLoadWeight');
        const theW = sumL(items, 'rawTheoryLoadWeight');
        const onTimeArr = sumL(items, 'rawOnTimeArrivalTripCount');
        const onTimeDepart = sumL(items, 'rawOnTimeDepartTripCount');
        const overDrive = sumS(items, 'rawOverDriveDurationCount');

        // 上期原始值求和（仅 total 行有值）
        const pTrip = sumL(items, 'prevTripCount');
        const pDPkg = sumL(items, 'prevRawDepartPackageQty');
        const pAPkg = sumS(items, 'prevRawAvgPackageQty');
        const pDLoadNum = sumL(items, 'prevRawDepartLoadNumerator');
        const pALoadNum = sumL(items, 'prevRawAvgLoadNumerator');
        const pLoadMax = sumL(items, 'prevRawPackageLoadMaxLimit');
        const pActW = sumL(items, 'prevRawActualLoadWeight');
        const pTheW = sumL(items, 'prevRawTheoryLoadWeight');
        const pOnTimeArr = sumL(items, 'prevRawOnTimeArrivalTripCount');
        const pOnTimeDepart = sumL(items, 'prevRawOnTimeDepartTripCount');
        const pOverDrive = sumS(items, 'prevRawOverDriveDurationCount');

        const calcWow = period === TIME_PERIOD_TOTAL && hasLast;

        result[period] = {
            ...items[0],
            tripCount: trip,
            departPackageQty: div2(dPkg, trip),
            avgPackageQty: div2(aPkg, trip),
            rawDepartPackageQty: dPkg,
            rawAvgPackageQty: String(aPkg),
            rawDepartLoadNumerator: dLoadNum,
            rawAvgLoadNumerator: aLoadNum,
            rawPackageLoadMaxLimit: loadMax,
            rawActualLoadWeight: actW,
            rawTheoryLoadWeight: theW,
            rawOnTimeArrivalTripCount: onTimeArr,
            rawOnTimeDepartTripCount: onTimeDepart,
            rawOverDriveDurationCount: String(overDrive),
            prevTripCount: pTrip || null,
            prevRawDepartPackageQty: pDPkg || null,
            prevRawAvgPackageQty: pAPkg ? String(pAPkg) : null,
            prevRawDepartLoadNumerator: pDLoadNum || null,
            prevRawAvgLoadNumerator: pALoadNum || null,
            prevRawPackageLoadMaxLimit: pLoadMax || null,
            prevRawActualLoadWeight: pActW || null,
            prevRawTheoryLoadWeight: pTheW || null,
            prevRawOnTimeArrivalTripCount: pOnTimeArr || null,
            prevRawOnTimeDepartTripCount: pOnTimeDepart || null,
            prevRawOverDriveDurationCount: pOverDrive ? String(pOverDrive) : null,
            avgWeightedPackageQty: div2(aLoadNum, trip),
            avgWeightedPackageQtyWow: calcWow ? avgWow(lALoadNum, lTrip, pALoadNum, pTrip) : null,
            departLoadRate: rate(dLoadNum, loadMax),
            avgLoadRate: rate(aLoadNum, loadMax),
            departWeightLoadRate: rate(actW, theW),
            onTimeArrivalRate: rate(onTimeArr, trip),
            onTimeDepartRate: rate(onTimeDepart, trip),
            avgOverDriveDuration: div2(overDrive, trip),
            // WoW：量类用百分比变化，率类用绝对差
            tripCountWow: calcWow ? wowChange(lTrip, pTrip) : null,
            rawDepartPackageQtyWow: calcWow ? wowChange(lDPkg, pDPkg) : null,
            departPackageQtyWow: calcWow ? avgWow(lDPkg, lTrip, pDPkg, pTrip) : null,
            avgPackageQtyWow: calcWow ? avgWow(lAPkg, lTrip, pAPkg, pTrip) : null,
            departLoadRateWow: calcWow ? wowDiff(lDLoadNum, lLoadMax, pDLoadNum, pLoadMax) : null,
            avgLoadRateWow: calcWow ? wowDiff(lALoadNum, lLoadMax, pALoadNum, pLoadMax) : null,
            departWeightLoadRateWow: calcWow ? wowDiff(lActW, lTheW, pActW, pTheW) : null,
            onTimeArrivalRateWow: calcWow ? wowDiff(lOnTimeArr, lTrip, pOnTimeArr, pTrip) : null,
            onTimeDepartRateWow: calcWow ? wowDiff(lOnTimeDepart, lTrip, pOnTimeDepart, pTrip) : null,
            avgOverDriveDurationWow: calcWow ? avgWow(lOverDrive, lTrip, pOverDrive, pTrip) : null,
        };
    }
    return result;
}

// 将 period key 转换为 [startDate, endDate]（YYYY-MM-DD）
function periodToDateRange(period: string, queryType: ScaleQueryType): [string, string] {
    if (queryType === ScaleQueryType.WEEKLY) {
        const parts = period.split('~');
        return [parts[0], parts[1]];
    }
    if (queryType === ScaleQueryType.MONTHLY) {
        const [year, month] = period.split('-').map(Number);
        const start = `${period}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        return [start, `${period}-${String(lastDay).padStart(2, '0')}`];
    }
    // DAILY
    return [period, period];
}

function buildTableRows(rawItems: TmsTransportSummaryItem[], lastPeriodKey: string): TableRow[] {
    const toHKey = (i: TmsTransportSummaryItem) => `${Number(i.startSortingId)}__${Number(i.routeId)}__${i.segmentName}`;
    const lastPeriodItemMap = new Map(rawItems.filter(i => i.timePeriod === lastPeriodKey).map(i => [toHKey(i), i]));
    const avgLoad = (loadNum: string | number | null | undefined, trip: number | null | undefined) =>
        trip && trip > 0 && loadNum != null ? Number(loadNum) / trip : null;

    // GraphQL Long 类型序列化为字符串，转数字以确保后续比较正确
    const items = rawItems.map(item => {
        const curAvg = avgLoad(item.rawAvgLoadNumerator, item.tripCount);
        // 环比仅 total 项计算：最后一期 vs 上一期（与后端口径一致）
        const lastItem = item.timePeriod === TIME_PERIOD_TOTAL ? lastPeriodItemMap.get(toHKey(item)) : undefined;
        const lastAvg = lastItem ? avgLoad(lastItem.rawAvgLoadNumerator, lastItem.tripCount) : null;
        const prevAvg = avgLoad(item.prevRawAvgLoadNumerator, item.prevTripCount);
        return {
            ...item,
            startSortingId: Number(item.startSortingId),
            routeId: Number(item.routeId),
            avgWeightedPackageQty: curAvg != null ? curAvg.toFixed(2) : null,
            avgWeightedPackageQtyWow: lastAvg != null && prevAvg != null && prevAvg !== 0
                ? ((lastAvg - prevAvg) / Math.abs(prevAvg) * 100).toFixed(2)
                : null,
        };
    });
    // 以 (startSortingId, routeId, segmentName) 为 key 聚合同一层级的所有时间段数据
    const hierarchyMap = new Map<string, RowMetrics>();

    for (const item of items) {
        const hKey = `${item.startSortingId}__${item.routeId}__${item.segmentName}`;
        if (!hierarchyMap.has(hKey)) hierarchyMap.set(hKey, {});
        hierarchyMap.get(hKey)![item.timePeriod] = item;
    }

    const makeRow = (startSortingId: number, routeId: number, segmentName: string, label: string, level: number): TableRow => {
        const hKey = `${startSortingId}__${routeId}__${segmentName}`;
        return {
            key: hKey,
            label,
            level,
            startSortingId,
            routeId,
            segmentName,
            canDrillDown: segmentName === '',
            metrics: hierarchyMap.get(hKey) ?? {},
        };
    };

    const rows: TableRow[] = [];

    const totalItems = items.filter(i => i.timePeriod === TIME_PERIOD_TOTAL);

    // 识别含 "test" 的 hub ID，在表格和筛选框中统一隐藏
    const testHubIds = new Set(
        totalItems
            .filter(i => i.startSortingId > 0 && i.routeId === 0 && (i.startSortingName ?? '').toLowerCase().includes('test'))
            .map(i => i.startSortingId)
    );
    const isTestHub = (startSortingId: number) => testHubIds.has(startSortingId);

    // Hub 和全美行始终从路线行重新聚合，不使用数据库预计算的聚合行
    // （预计算行可能包含该 Hub 下所有路线，筛选后指标不准确）

    /** 构建路线行（含同名合并）和分段行，返回 routeRows 列表 */
    const buildRouteRowsForHub = (hubId: number, level: number): TableRow[] => {
        const routeItemsForHub = totalItems.filter(i => i.startSortingId === hubId && i.routeId > 0 && i.segmentName === '');
        const routeNameMap = new Map<string, number[]>();
        for (const ri of routeItemsForHub) {
            const name = ri.routeName ?? `Route ${ri.routeId}`;
            if (!routeNameMap.has(name)) routeNameMap.set(name, []);
            routeNameMap.get(name)!.push(ri.routeId);
        }
        return [...routeNameMap.entries()]
            .map(([routeName, rIds]) => {
                const primaryId = rIds[0];
                const mergedMetrics = mergeRowMetrics(rIds.map(rid => hierarchyMap.get(`${hubId}__${rid}__`) ?? {}), lastPeriodKey);
                const routeRow: TableRow = {
                    key: `${hubId}__${primaryId}__`, label: routeName, level,
                    startSortingId: hubId, routeId: primaryId, allRouteIds: rIds,
                    segmentName: '', canDrillDown: true, metrics: mergedMetrics,
                };
                if (SHOW_SEGMENTS) {
                    const allSegItems = rIds.flatMap(rid => totalItems.filter(i => i.startSortingId === hubId && i.routeId === rid && i.segmentName !== ''));
                    const segNameMap = new Map<string, number[]>();
                    for (const si of allSegItems) { if (!segNameMap.has(si.segmentName)) segNameMap.set(si.segmentName, []); segNameMap.get(si.segmentName)!.push(si.routeId); }
                    const segRows = [...segNameMap.entries()].map(([sn, srids]) => ({ key: `${hubId}__${primaryId}__${sn}`, label: sn, level: level + 1, startSortingId: hubId, routeId: primaryId, segmentName: sn, canDrillDown: false, metrics: mergeRowMetrics(srids.map(rid => hierarchyMap.get(`${hubId}__${rid}__${sn}`) ?? {}), lastPeriodKey) } as TableRow)).sort((a, b) => (b.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0) - (a.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0));
                    if (segRows.length > 0) routeRow.children = segRows;
                }
                return routeRow;
            })
            .sort((a, b) => (b.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0) - (a.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0));
    };

    /** 从路线行列表创建 Hub 行（指标从路线行重新聚合） */
    const buildHubRow = (hubId: number, hubName: string, level: number, routeRows: TableRow[]): TableRow => ({
        key: `${hubId}__0__`, label: hubName, level,
        startSortingId: hubId, routeId: 0, segmentName: '', canDrillDown: true,
        metrics: routeRows.length > 0 ? mergeRowMetrics(routeRows.map(r => r.metrics), lastPeriodKey) : {},
        children: routeRows.length > 0 ? routeRows : undefined,
    });

    // 全美 row (startSortingId=0)
    const globalItem = totalItems.find(i => i.startSortingId === 0 && i.routeId === 0);
    if (globalItem) {
        // 始发地 rows，按车次降序，排除 test hub
        // Hub 名称从预计算行读取（仅读名称），指标从路线行重新聚合
        const hubs = [...new Set(totalItems.filter(i => i.startSortingId > 0 && i.routeId === 0 && !isTestHub(i.startSortingId)).map(i => i.startSortingId))]
            .sort((a, b) => {
                // 临时用路线行求和排序（makeRow 不再用于 hub 行）
                const routeA = totalItems.filter(i => i.startSortingId === a && i.routeId > 0 && i.segmentName === '');
                const routeB = totalItems.filter(i => i.startSortingId === b && i.routeId > 0 && i.segmentName === '');
                const tA = routeA.reduce((s, i) => s + (i.tripCount ?? 0), 0);
                const tB = routeB.reduce((s, i) => s + (i.tripCount ?? 0), 0);
                return tB - tA;
            });
        const hubRows = hubs.map(hubId => {
            const hubItem = totalItems.find(i => i.startSortingId === hubId && i.routeId === 0);
            const hubName = hubItem?.startSortingName ?? `Hub ${hubId}`;
            return buildHubRow(hubId, hubName, 1, buildRouteRowsForHub(hubId, 2));
        });
        // 全美行指标从所有路线行重新聚合（不使用预计算的 startSortingId=0 行）
        const allRouteRows = hubRows.flatMap(h => h.children ?? []).filter(r => r.routeId > 0);
        const globalMetrics = allRouteRows.length > 0 ? mergeRowMetrics(allRouteRows.map(r => r.metrics), lastPeriodKey) : {};
        const globalRow: TableRow = {
            key: '0__0__', label: '全美', level: 0,
            startSortingId: 0, routeId: 0, segmentName: '', canDrillDown: true,
            metrics: globalMetrics,
            children: hubRows.length > 0 ? hubRows : undefined,
        };
        if (hubRows.length > 0) rows.push(globalRow);
    } else {
        // 始发地过滤（有 hub 聚合行但无全美行）或路线过滤（无聚合行）
        // 统一：从路线行构建，Hub 行指标从路线行聚合
        const hubAggregates = totalItems.filter(i => i.startSortingId > 0 && i.routeId === 0 && !isTestHub(i.startSortingId));
        const hasHubAggregates = hubAggregates.length > 0;

        if (hasHubAggregates) {
            // Hub 过滤场景：有 hub 聚合行，以 hub 为顶层
            const hubIds = [...new Set(hubAggregates.map(i => i.startSortingId))];
            for (const hubId of hubIds) {
                const hubItem = hubAggregates.find(i => i.startSortingId === hubId);
                const routeRows = buildRouteRowsForHub(hubId, 1);
                rows.push(buildHubRow(hubId, hubItem?.startSortingName ?? `Hub ${hubId}`, 0, routeRows));
            }
        } else {
            // 路线过滤场景（routeId 直接过滤后无聚合行）：只展示路线行
            const routeLevelItems = totalItems.filter(i => i.routeId > 0 && i.segmentName === '' && !isTestHub(i.startSortingId));
            const routeNameMapF = new Map<string, { primaryId: number; hubId: number; ids: number[] }>();
            for (const ri of routeLevelItems) {
                const name = ri.routeName ?? `Route ${ri.routeId}`;
                if (!routeNameMapF.has(name)) routeNameMapF.set(name, { primaryId: ri.routeId, hubId: ri.startSortingId, ids: [] });
                routeNameMapF.get(name)!.ids.push(ri.routeId);
            }
            [...routeNameMapF.entries()]
                .map(([routeName, { primaryId, hubId, ids }]) => {
                    const mergedM = mergeRowMetrics(ids.map(rid => hierarchyMap.get(`${hubId}__${rid}__`) ?? {}), lastPeriodKey);
                    const routeRow: TableRow = { key: `${hubId}__${primaryId}__`, label: routeName, level: 0, startSortingId: hubId, routeId: primaryId, allRouteIds: ids, segmentName: '', canDrillDown: true, metrics: mergedM };
                    if (SHOW_SEGMENTS) {
                        const allSI = ids.flatMap(rid => totalItems.filter(i => i.routeId === rid && i.segmentName !== ''));
                        const sNameMapF = new Map<string, number[]>();
                        for (const si of allSI) { if (!sNameMapF.has(si.segmentName)) sNameMapF.set(si.segmentName, []); sNameMapF.get(si.segmentName)!.push(si.routeId); }
                        const segRows = [...sNameMapF.entries()].map(([sn, srids]) => ({ key: `${hubId}__${primaryId}__${sn}`, label: sn, level: 1, startSortingId: hubId, routeId: primaryId, segmentName: sn, canDrillDown: false, metrics: mergeRowMetrics(srids.map(rid => hierarchyMap.get(`${hubId}__${rid}__${sn}`) ?? {}), lastPeriodKey) } as TableRow)).sort((a, b) => (b.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0) - (a.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0));
                        if (segRows.length > 0) routeRow.children = segRows;
                    }
                    return routeRow;
                })
                .sort((a, b) => (b.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0) - (a.metrics[TIME_PERIOD_TOTAL]?.tripCount ?? 0))
                .forEach(r => rows.push(r));
        }
    }

    return rows;
}

const TransportationDataPage: React.FC = () => {
    const [routeType, setRouteType] = useState(1);
    const [timeRange, setTimeRange] = useState<TimeRange>({
        queryType: ScaleQueryType.DAILY,
        startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    });
    const [selectedHubId, setSelectedHubId] = useState<number | null>(null);
    const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
    const [selectedRouteName, setSelectedRouteName] = useState<string | null>(null);
    const [selectedRouteAllIds, setSelectedRouteAllIds] = useState<number[]>([]); // 同名路线的全部 ID
    const [drawerOpen, setDrawerOpen] = useState(false);
    // 每个指标的日期子列展开/收起状态（默认全部展开）
    const [dateColumnsExpanded, setDateColumnsExpanded] = useState<Record<MetricKey, boolean>>(
        () => Object.fromEntries(METRIC_CONFIGS.map(m => [m.key, true])) as Record<MetricKey, boolean>
    );
    // 按合计排序（仅车次/发车包裹量/车均包裹量支持）
    const [sortState, setSortState] = useState<{ metric: MetricKey | null; order: 'descend' | 'ascend' | null }>({ metric: null, order: null });
    const SORTABLE_METRICS: Set<MetricKey> = new Set(['tripCount', 'departPackageQty', 'avgPackageQty']);
    const cycleSortByMetric = useCallback((metric: MetricKey) => {
        setSortState(prev => {
            if (prev.metric !== metric) return { metric, order: 'descend' };
            if (prev.order === 'descend') return { metric, order: 'ascend' };
            return { metric: null, order: null };
        });
    }, []);
    const [drawerContext, setDrawerContext] = useState<TransportDetailContext | null>(null);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    const dashboardQuery = useQuery<{
        queryTmsTransportDashboard: { calculatedAt: string | null; items: TmsTransportSummaryItem[] };
    }>(QUERY_TMS_TRANSPORT_DASHBOARD, {
        variables: {
            input: {
                routeType,
                queryType: timeRange.queryType,
                startDate: timeRange.startDate,
                endDate: timeRange.endDate,
                startSortingId: selectedHubId,
                // 同名路线有多个 ID 时不过滤（返回 hub 全部路线，前端按名称展示选中的）
                // 唯一路线时正常传 routeId，减少数据量
                routeId: selectedRouteAllIds.length > 1 ? null : selectedRouteId,
            },
        },
    });

    // Hub 列表：同时查干线(1)和支线(2)取并集，保持两个 Tab 一致
    const hubQueryMainline = useQuery<{
        queryTmsTransportFilterOptions: { hubs: { id: number; name: string }[]; routes: { id: number; name: string; allIds: number[] }[] };
    }>(QUERY_TMS_TRANSPORT_FILTER_OPTIONS, {
        variables: { input: { routeType: 1, startSortingId: null } },
    });
    const hubQueryBranch = useQuery<{
        queryTmsTransportFilterOptions: { hubs: { id: number; name: string }[]; routes: { id: number; name: string; allIds: number[] }[] };
    }>(QUERY_TMS_TRANSPORT_FILTER_OPTIONS, {
        variables: { input: { routeType: 2, startSortingId: null } },
    });

    // 当前 Tab 的路线选项仍按 routeType + selectedHub 查询
    const routeFilterQuery = useQuery<{
        queryTmsTransportFilterOptions: { hubs: { id: number; name: string }[]; routes: { id: number; name: string; allIds: number[] }[] };
    }>(QUERY_TMS_TRANSPORT_FILTER_OPTIONS, {
        variables: { input: { routeType, startSortingId: selectedHubId } },
        skip: !selectedHubId,
    });

    const items = dashboardQuery.data?.queryTmsTransportDashboard?.items ?? [];
    const calculatedAt = dashboardQuery.data?.queryTmsTransportDashboard?.calculatedAt;

    // 合并两个 Tab 的 Hub 列表，去重，过滤 test，按名称排序
    const hubs = useMemo(() => {
        const all = [
            ...(hubQueryMainline.data?.queryTmsTransportFilterOptions?.hubs ?? []),
            ...(hubQueryBranch.data?.queryTmsTransportFilterOptions?.hubs ?? []),
        ];
        const seen = new Set<number>();
        return all
            .filter(h => {
                if (seen.has(h.id)) return false;
                seen.add(h.id);
                return !h.name.toLowerCase().includes('test');
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [hubQueryMainline.data, hubQueryBranch.data]);

    const routes = routeFilterQuery.data?.queryTmsTransportFilterOptions?.routes ?? [];

    const lastPeriodKey = toLastPeriodKey(timeRange.queryType, timeRange.endDate);
    const tableRows = useMemo(() => buildTableRows(items, lastPeriodKey), [items, lastPeriodKey]);

    // 数据加载后默认展开到路线层（全美 / 始发地行展开，路线行不展开）
    useEffect(() => {
        const keys: string[] = [];
        const collect = (rows: TableRow[]) => {
            for (const row of rows) {
                if (row.routeId === 0 && row.children && row.children.length > 0) {
                    keys.push(row.key);
                    collect(row.children);
                }
            }
        };
        collect(tableRows);
        setExpandedKeys(keys);
    }, [tableRows]);

    const periods = useMemo(() => {
        const allPeriods = new Set(items.map(i => i.timePeriod).filter(p => p !== TIME_PERIOD_TOTAL));
        return [...allPeriods].sort();
    }, [items]);

    // 应用排序（递归对每层的 children 也排序）
    const sortedTableRows = useMemo(() => {
        // 可见性规则（始终显式处理，不依赖后端是否返回聚合行）：
        // - 选了线路 → 只展示路线行（无 Hub、无全美）
        // - 选了 Hub 但未选线路 → 展示 Hub + 路线（无全美）
        // - 未选任何筛选 → 全树展示
        const filtered = (() => {
            const collectRouteRows = (rows: TableRow[], nameFilter?: string | null): TableRow[] => {
                const result: TableRow[] = [];
                for (const r of rows) {
                    if (r.routeId > 0 && r.segmentName === '' && (!nameFilter || r.label === nameFilter)) result.push(r);
                    if (r.children) result.push(...collectRouteRows(r.children, nameFilter));
                }
                return result;
            };

            if (selectedRouteId || (selectedRouteName && selectedRouteAllIds.length > 1)) {
                // 选了线路：只返回匹配路线行
                const nameFilter = selectedRouteAllIds.length > 1 ? selectedRouteName : null;
                return collectRouteRows(tableRows, nameFilter);
            }

            if (selectedHubId) {
                // 选了 Hub：去掉全美行，只保留 Hub 及以下
                const withoutGlobal = tableRows.filter(r => r.startSortingId !== 0);
                if (withoutGlobal.length > 0) return withoutGlobal;
                // 受限用户：后端内存聚合后全美行是唯一顶层节点，需取其 Hub 子行
                const globalRow = tableRows.find(r => r.startSortingId === 0 && r.routeId === 0);
                return (globalRow?.children ?? []).filter(r => r.startSortingId !== 0);
            }

            return tableRows; // 无筛选：完整树
        })();

        if (!sortState.metric || !sortState.order) return filtered;
        const metric = sortState.metric;
        const order = sortState.order;
        const getValue = (row: TableRow) =>
            parseFloat((row.metrics[TIME_PERIOD_TOTAL]?.[metric as keyof TmsTransportSummaryItem] as string) ?? '0') || 0;
        const sortRows = (rows: TableRow[]): TableRow[] =>
            [...rows]
                .sort((a, b) => order === 'descend' ? getValue(b) - getValue(a) : getValue(a) - getValue(b))
                .map(row => ({ ...row, children: row.children ? sortRows(row.children) : undefined }));
        return sortRows(filtered);
    }, [tableRows, sortState, selectedRouteName, selectedRouteAllIds]);

    const handleCellClick = useCallback((row: TableRow, metricConfig: MetricConfig) => {
        if (!row.canDrillDown) return; // segment rows can't drill down
        setDrawerContext({
            routeType,
            startSortingId: row.startSortingId,
            routeId: row.routeId,
            routeIds: row.allRouteIds,
            startDate: timeRange.startDate,
            endDate: timeRange.endDate,
            metricLabel: metricConfig.title,
        });
        setDrawerOpen(true);
    }, [routeType, timeRange]);

    const columns = useMemo((): ColumnsType<TableRow> => {
        const nameCol: ColumnsType<TableRow>[0] = {
            title: '始发地/路线',
            key: 'label',
            dataIndex: 'label',
            fixed: 'left',
            width: 200,
        };

        const metricCols = METRIC_CONFIGS.map(metricConfig => {
            const isSortable = SORTABLE_METRICS.has(metricConfig.key);
            const isThisMetricSorted = sortState.metric === metricConfig.key;
            const isExpanded = dateColumnsExpanded[metricConfig.key];

            const totalTitle = (
                <div className="flex items-center justify-end gap-1 flex-nowrap">
                    <span>合计</span>
                    {isSortable && (
                        <Tooltip title="按合计排序">
                            <span
                                className={`cursor-pointer ${isThisMetricSorted ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'}`}
                                onClick={(e) => { e.stopPropagation(); cycleSortByMetric(metricConfig.key); }}
                            >
                                {isThisMetricSorted && sortState.order === 'ascend'
                                    ? <CaretUpOutlined />
                                    : <CaretDownOutlined />}
                            </span>
                        </Tooltip>
                    )}
                    {periods.length > 0 && (
                        <Tooltip title={isExpanded ? '收起日期列' : '展开日期列'}>
                            <span
                                className="cursor-pointer text-gray-400 hover:text-gray-600"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDateColumnsExpanded(prev => ({ ...prev, [metricConfig.key]: !prev[metricConfig.key] }));
                                }}
                            >
                                {isExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                            </span>
                        </Tooltip>
                    )}
                </div>
            );

            return {
                title: <span>{metricConfig.title}<MetricTooltip content={metricConfig.description} /></span>,
                key: metricConfig.key,
                children: [
                    {
                        title: totalTitle,
                        key: `${metricConfig.key}_total`,
                        width: 130,
                        render: (_: unknown, row: TableRow) => {
                            if (row.segmentName !== '' && !SEGMENT_ALLOWED_METRICS.has(metricConfig.key)) return '-';
                            const total = row.metrics[TIME_PERIOD_TOTAL];
                            const value = total?.[metricConfig.key as keyof TmsTransportSummaryItem];
                            const canClick = row.canDrillDown;
                            return (
                                <span
                                    style={{ cursor: canClick ? 'pointer' : 'default', color: canClick ? '#1890ff' : undefined }}
                                    onClick={canClick ? () => handleCellClick(row, metricConfig) : undefined}
                                >
                                    {formatMetric(value as string | number | null | undefined, metricConfig.type)}
                                </span>
                            );
                        },
                    },
                    ...(isExpanded ? periods.map(period => {
                        const [pStart, pEnd] = periodToDateRange(period, timeRange.queryType);
                        return {
                            title: period,
                            key: `${metricConfig.key}_${period}`,
                            width: 110,
                            render: (_: unknown, row: TableRow) => {
                                if (row.segmentName !== '' && !SEGMENT_ALLOWED_METRICS.has(metricConfig.key)) return '-';
                                const periodData = row.metrics[period];
                                const value = periodData?.[metricConfig.key as keyof TmsTransportSummaryItem];
                                const canClick = row.canDrillDown;
                                return (
                                    <span
                                        style={{ cursor: canClick ? 'pointer' : 'default', color: canClick ? '#1890ff' : undefined }}
                                        onClick={canClick ? () => {
                                            setDrawerContext({
                                                routeType,
                                                startSortingId: row.startSortingId,
                                                routeId: row.routeId,
                                                routeIds: row.allRouteIds,
                                                startDate: pStart,
                                                endDate: pEnd,
                                                metricLabel: metricConfig.title,
                                            });
                                            setDrawerOpen(true);
                                        } : undefined}
                                    >
                                        {formatMetric(value as string | number | null | undefined, metricConfig.type)}
                                    </span>
                                );
                            },
                        };
                    }) : []),
                    ...(metricConfig.wowKey && isExpanded
                        ? [{
                            title: timeRange.queryType === ScaleQueryType.DAILY ? '周环比' : '环比',
                            key: `${metricConfig.key}_wow`,
                            width: 90,
                            render: (_: unknown, row: TableRow) => {
                                if (row.segmentName !== '' && !SEGMENT_ALLOWED_METRICS.has(metricConfig.key)) return '-';
                                const total = row.metrics[TIME_PERIOD_TOTAL];
                                const wow = total?.[metricConfig.wowKey as keyof TmsTransportSummaryItem] as string | null | undefined;
                                return formatWow(wow);
                            },
                        }]
                        : []),
                ],
            };
        });

        return [nameCol, ...metricCols];
    }, [periods, handleCellClick, dateColumnsExpanded, sortState, timeRange, routeType]);

    const handleTabChange = (key: string) => {
        setRouteType(Number(key));
        setSelectedRouteId(null);
        setSelectedRouteAllIds([]);
        setSelectedRouteName(null);
        setExpandedKeys([]);
    };

    const handleHubChange = (value: number | null) => {
        setSelectedHubId(value);
        setSelectedRouteId(null);
        setSelectedRouteAllIds([]);
        setSelectedRouteName(null);
    };

    const dataUpdatedAtText = calculatedAt ? formatUtcToPacific(calculatedAt) : '';

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-4">
                <Title level={4} style={{ margin: 0 }}>
                    运输数据看板
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                        （时区说明: 日期均为美西时区）
                    </Text>
                </Title>
                {dataUpdatedAtText && (
                    <span className="text-sm text-red-500 font-bold">
                        数据更新于 {dataUpdatedAtText}
                    </span>
                )}
            </div>

            <Tabs
                activeKey={String(routeType)}
                onChange={handleTabChange}
                items={[
                    { key: '1', label: '干线' },
                    { key: '2', label: '支线' },
                ]}
            />

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <TimeRangeFilter
                    onChange={setTimeRange}
                    dashboardTimezone="America/Los_Angeles"
                    maxDaysBack={365}
                    presetKeysByQueryType={{
                        [ScaleQueryType.DAILY]: ['yesterday', 'last3days', 'last7days', 'last15days', 'custom'],
                    }}
                />
                <Select
                    placeholder="始发HUB"
                    allowClear
                    style={{ width: 160 }}
                    value={selectedHubId ?? undefined}
                    onChange={handleHubChange}
                    options={hubs.map(h => ({ label: h.name, value: h.id }))}
                />
                <Select
                    placeholder="线路名称"
                    allowClear
                    style={{ width: 200 }}
                    value={selectedRouteId ?? undefined}
                    onChange={(v) => {
                        setSelectedRouteId(v ?? null);
                        const opt = routes.find(r => r.id === v);
                        setSelectedRouteAllIds(opt?.allIds ?? (v != null ? [v] : []));
                        setSelectedRouteName(opt?.name ?? null);
                    }}
                    disabled={!selectedHubId}
                    options={routes.map(r => ({ label: r.name, value: r.id }))}
                />
            </div>

            <Table<TableRow>
                columns={columns}
                dataSource={sortedTableRows}
                loading={dashboardQuery.loading}
                rowKey="key"
                bordered
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="small"
                expandable={{
                    expandedRowKeys: expandedKeys,
                    onExpand: (expanded, record) => {
                        setExpandedKeys(prev =>
                            expanded ? [...prev, record.key] : prev.filter(k => k !== record.key)
                        );
                    },
                    defaultExpandAllRows: false,
                }}
                onRow={(record) => ({
                    style: {
                        background: record.level === 0 ? '#fafafa' : undefined,
                        fontWeight: record.level === 0 ? 600 : undefined,
                    },
                })}
            />

            <TransportDetailDrawer
                open={drawerOpen}
                context={drawerContext}
                onClose={() => setDrawerOpen(false)}
            />
        </div>
    );
};

export default TransportationDataPage;
